import { useCallback, useEffect, useRef, useState } from 'react';
import {
  contentRect,
  toNormalized,
  toClient,
  toSvgPoints,
  distanceToPoint,
  CLOSE_THRESHOLD_PX,
  MIN_POINTS,
  MAX_POINTS,
  type ContentRect,
  type NormPoint,
} from './roiGeometry';

/**
 * Draws the detection area over the live video.
 *
 * The area is a polygon in normalised (0..1) frame coordinates. The backend crops the frame
 * to its padded bounding box before inference — a resolution win, not a GPU saving — then
 * maps the boxes back and drops the ones outside the exact polygon. See backend/detection_roi.py.
 *
 * Only meaningful over the server-composited MJPEG feed: that is the surface detection
 * overlays are burned into, so it is the only one where "this part of the picture" has an
 * agreed meaning on both ends.
 */

/** The RTMP reader's output size (main.py, out_w/out_h) — used until the <img> has decoded,
 *  so the overlay is never mis-scaled during the first frames. */
const FALLBACK_FRAME = { width: 640, height: 360 };

export interface RoiEditorProps {
  /** The `.video-wrapper` the video and this overlay share. */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
  onClose: () => void;
  /** Told whether the feed now has an area, so the panel can light its button. */
  onAreaChange?: (hasArea: boolean) => void;
}

interface RoiResponse {
  stream_key: string;
  roi: { polygon: number[][]; name: string; pad: number } | null;
}

export default function RoiEditor({
  wrapperRef, apiUrl, authHeaders, onClose, onAreaChange,
}: RoiEditorProps) {
  const [points, setPoints] = useState<NormPoint[]>([]);
  const [drawing, setDrawing] = useState(true);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragIndex = useRef<number | null>(null);
  const dragMoved = useRef(false);

  // --- keep the overlay glued to the picture, not to the element -------- //
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    const natW = img?.naturalWidth || FALLBACK_FRAME.width;
    const natH = img?.naturalHeight || FALLBACK_FRAME.height;
    setRect(contentRect(wrapper.clientWidth, wrapper.clientHeight, natW, natH));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    // The <img> reports naturalWidth only once a frame has decoded, which is after mount.
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- load whatever this feed already has ------------------------------ //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/drone/detection/roi`, { headers: authHeaders() });
        if (!res.ok) {
          if (!cancelled) {
            setStatus(res.status === 409
              ? 'Select a video source first'
              : `Could not load the current area (${res.status})`);
          }
          return;
        }
        const data: RoiResponse = await res.json();
        if (cancelled) return;
        if (data.roi?.polygon?.length) {
          setPoints(data.roi.polygon.map(([x, y]) => [x, y] as NormPoint));
          setDrawing(false);
        }
      } catch {
        if (!cancelled) setStatus('Could not reach the backend');
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders]);

  // --- drawing ---------------------------------------------------------- //
  const localPoint = (e: React.PointerEvent | React.MouseEvent): [number, number] => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return [0, 0];
    return [e.clientX - box.left, e.clientY - box.top];
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!drawing || dragMoved.current) return;
    const [x, y] = localPoint(e);
    if (points.length >= MIN_POINTS && distanceToPoint(points[0], x, y, rect) <= CLOSE_THRESHOLD_PX) {
      setDrawing(false);
      setCursor(null);
      return;
    }
    if (points.length >= MAX_POINTS) {
      setStatus(`An area can have at most ${MAX_POINTS} points`);
      return;
    }
    setPoints(prev => [...prev, toNormalized(x, y, rect)]);
    setStatus(null);
  };

  const handleMove = (e: React.PointerEvent) => {
    const [x, y] = localPoint(e);
    if (dragIndex.current !== null) {
      dragMoved.current = true;
      const idx = dragIndex.current;
      const next = toNormalized(x, y, rect);
      setPoints(prev => prev.map((p, i) => (i === idx ? next : p)));
      return;
    }
    if (drawing) setCursor([x, y]);
  };

  const startDrag = (index: number) => (e: React.PointerEvent) => {
    if (drawing) return;           // while drawing, a click on a vertex means "close"
    e.stopPropagation();
    dragIndex.current = index;
    dragMoved.current = false;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const endDrag = () => {
    dragIndex.current = null;
    // Let the click that follows a real drag be swallowed, but not a plain click.
    setTimeout(() => { dragMoved.current = false; }, 0);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && drawing && points.length >= MIN_POINTS) setDrawing(false);
      if (e.key === 'Backspace' && drawing) setPoints(prev => prev.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawing, points.length, onClose]);

  // --- persistence ------------------------------------------------------ //
  const save = async () => {
    if (points.length < MIN_POINTS) return;
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${apiUrl}/api/drone/detection/roi`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ polygon: points.map(([x, y]) => [x, y]), name: 'Detection area' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setStatus((body && body.detail) || `Save failed (${res.status})`);
        return;
      }
      onAreaChange?.(true);
      onClose();
    } catch {
      setStatus('Save failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/drone/detection/roi`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok && res.status !== 404) {
        setStatus(`Clear failed (${res.status})`);
        return;
      }
      setPoints([]);
      setDrawing(true);
      onAreaChange?.(false);
    } catch {
      setStatus('Clear failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  };

  // --- render ----------------------------------------------------------- //
  const closed = !drawing && points.length >= MIN_POINTS;
  const maskId = 'roi-cutout';
  const btn = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 3, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
    letterSpacing: '0.04em', ...extra,
  });
  const remaining = MIN_POINTS - points.length;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
      <svg
        ref={svgRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: drawing ? 'crosshair' : 'default', touchAction: 'none',
        }}
        onClick={handleClick}
        onPointerMove={handleMove}
        onPointerUp={endDrag}
        onPointerLeave={() => { endDrag(); setCursor(null); }}
      >
        {/* Everything outside the area is dimmed — the operator sees exactly what the
            model is being told to ignore, which is the whole point of drawing it. */}
        {closed && (
          <>
            <defs>
              <mask id={maskId}>
                <rect x={0} y={0} width="100%" height="100%" fill="white" />
                <polygon points={toSvgPoints(points, rect)} fill="black" />
              </mask>
            </defs>
            <rect
              x={rect.left} y={rect.top} width={rect.width} height={rect.height}
              fill="rgba(0,0,0,0.55)" mask={`url(#${maskId})`} pointerEvents="none"
            />
          </>
        )}

        {points.length > 1 && (
          <polyline
            points={toSvgPoints(closed ? [...points, points[0]] : points, rect)}
            fill={closed ? 'rgba(0,200,255,0.10)' : 'none'}
            stroke="#00c8ff" strokeWidth={2} pointerEvents="none"
          />
        )}

        {/* The rubber-band segment from the last vertex to the cursor. */}
        {drawing && points.length > 0 && cursor && (() => {
          const last = points[points.length - 1];
          const [lx, ly] = toClient(last[0], last[1], rect);
          return (
            <line x1={lx} y1={ly} x2={cursor[0]} y2={cursor[1]}
                  stroke="#00c8ff" strokeWidth={1.5} strokeDasharray="4 3" pointerEvents="none" />
          );
        })()}

        {points.map((p, i) => {
          const [x, y] = toClient(p[0], p[1], rect);
          const canClose = drawing && i === 0 && points.length >= MIN_POINTS;
          return (
            <circle
              key={i} cx={x} cy={y} r={canClose ? 8 : 5}
              fill={canClose ? '#00c8ff' : 'rgba(0,0,0,0.6)'}
              stroke="#00c8ff" strokeWidth={2}
              style={{ cursor: drawing ? 'crosshair' : 'move' }}
              onPointerDown={startDrag(i)}
            />
          );
        })}
      </svg>

      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6,
        alignItems: 'center', flexWrap: 'wrap', maxWidth: 'calc(100% - 16px)',
      }}>
        <span style={{
          background: 'rgba(0,0,0,0.65)', color: 'var(--color-info)', fontSize: 11,
          padding: '3px 8px', borderRadius: 3, letterSpacing: '0.05em',
        }}>
          {drawing
            ? remaining > 0
              ? `DETECTION AREA — click ${remaining} more point${remaining === 1 ? '' : 's'}`
              : 'DETECTION AREA — click the first point to close, or press Enter'
            : 'DETECTION AREA — drag a point to adjust'}
        </span>
        {closed && (
          <button style={btn({ borderColor: '#00c8ff', color: '#00c8ff' })}
                  onClick={save} disabled={busy}>
            {busy ? 'SAVING…' : 'SAVE'}
          </button>
        )}
        {points.length > 0 && (
          <button style={btn()} onClick={() => { setPoints([]); setDrawing(true); setCursor(null); }}>
            REDRAW
          </button>
        )}
        <button style={btn()} onClick={clear} disabled={busy}>CLEAR</button>
        <button style={btn()} onClick={onClose}>CANCEL</button>
        {status && (
          <span style={{
            background: 'rgba(120,0,0,0.75)', color: '#ffd7d7', fontSize: 11,
            padding: '3px 8px', borderRadius: 3,
          }}>{status}</span>
        )}
      </div>
    </div>
  );
}
